import DOMPurify from "dompurify";
import { ReactElement, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import { OutputEmail, OutputEmailSuggestion, WorkflowOutput } from "./interface";
import { getAttachmentDisplayName } from "./reviewCtaUtils";
import { putData } from "./backend";
import useRichTextfield from "./hooks/useRichTextfield";
import FormatButton from "./FormatButton";
import TruncateTooltip from "./TruncateTooltip";
import ListBulletIcon from "./ListBulletIcon";
import ListNumberedIcon from "./ListNumberedIcon";
import { MIMETYPE_ICON_MAP } from "./utils";
import {
  EMAIL_PREVIEW_BODY_CONTENT_CLASS,
  EMAIL_PREVIEW_BODY_CONTENT_EDITABLE_CLASS,
  EMAIL_PREVIEW_BODY_OUTER_CLASS,
  EMAIL_PREVIEW_FIELD_ROW_CLASS,
  EMAIL_PREVIEW_HEADER_CLASS,
  EMAIL_PREVIEW_LABEL_CLASS,
  emailPreviewBodyFrameClass,
  emailPreviewFieldClass,
  formatWorkflowUserSender,
  AI_PREVIEW_BODY_CONTENT_CLASS,
  AI_PREVIEW_LABEL_CLASS,
  AI_PREVIEW_OUTER_CLASS,
  AI_PREVIEW_SHELL_ENTER_CLASS,
  aiPreviewBodyFrameClass,
  aiPreviewFieldClass,
} from "./emailPreviewLayout";

const ALLOWED_TAGS = [
  "a", "b", "br", "div", "em", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "span", "strong", "table", "tbody", "td", "th", "thead", "tr", "u", "ul",
];

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["style", "src", "alt", "href", "target", "rel", "width", "height", "class"],
  });
}

interface Props {
  token: string;
  email: OutputEmail;
  outputs?: WorkflowOutput[];
  readOnly?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  suggestionPreview?: {
    phase: "generating" | "ready" | "actioning";
    suggestion?: OutputEmailSuggestion;
  };
}

export interface OutputEmailEditorHandle {
  flushSave: () => Promise<void>;
  isDirty: () => boolean;
  applySuggestion: (subject: string, content: string) => void;
}

const AUTOSAVE_DEBOUNCE_MS = 1000;

const OutputEmailEditor = forwardRef<OutputEmailEditorHandle, Props>(function OutputEmailEditor(props, ref) {
  const { token, email, outputs = [], readOnly, onDirtyChange, suggestionPreview } = props;
  const { t } = useTranslation();

  const isSuggestionMode = !!suggestionPreview;
  const isGenerating = suggestionPreview?.phase === "generating";
  const showSuggestionContent = isSuggestionMode && !isGenerating;

  const [contentRevealed, setContentRevealed] = useState(false);

  useEffect(() => {
    if (!isSuggestionMode || isGenerating) {
      setContentRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setContentRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [isSuggestionMode, isGenerating, suggestionPreview?.phase]);

  const [emailTo, setEmailTo] = useState(email.emailTo || "");
  const [emailCc, setEmailCc] = useState(email.emailCc || "");
  const [emailBcc, setEmailBcc] = useState(email.emailBcc || "");
  const [emailSubject, setEmailSubject] = useState(email.emailSubject || "");
  const [emailContent, setEmailContent] = useState(email.emailContent || "");
  const [showCc, setShowCc] = useState(!!email.emailCc?.trim());
  const [showBcc, setShowBcc] = useState(!!email.emailBcc?.trim());

  const [savedSnapshot, setSavedSnapshot] = useState({
    to: email.emailTo || "",
    cc: email.emailCc || "",
    bcc: email.emailBcc || "",
    subject: email.emailSubject || "",
    content: email.emailContent || "",
  });

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>({});

  const editorRef = useRef<HTMLDivElement | null>(null);
  const { RichTextfield, applyFormat, resetEditorContent } = useRichTextfield({
    editorRef,
    content: emailContent,
    setContent: setEmailContent,
    disabled: readOnly || isSuggestionMode,
  });

  const isDirty = useMemo(() => {
    return (
      emailTo !== savedSnapshot.to ||
      emailCc !== savedSnapshot.cc ||
      emailBcc !== savedSnapshot.bcc ||
      emailSubject !== savedSnapshot.subject ||
      emailContent !== savedSnapshot.content
    );
  }, [emailTo, emailCc, emailBcc, emailSubject, emailContent, savedSnapshot]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (isSuggestionMode) return;
    const update = () => {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    };
    document.addEventListener("selectionchange", update);
    return () => document.removeEventListener("selectionchange", update);
  }, [isSuggestionMode]);

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSave = useCallback(async (): Promise<void> => {
    if (readOnly || isSuggestionMode || !isDirty) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    setSaveStatus("saving");
    const snapshot = { to: emailTo, cc: emailCc, bcc: emailBcc, subject: emailSubject, content: emailContent };
    try {
      await putData(`/hubspot/app/output-emails/${email.id}/`, token, {
        emailTo: snapshot.to,
        emailCc: snapshot.cc,
        emailBcc: snapshot.bcc,
        emailSubject: snapshot.subject,
        emailContent: snapshot.content,
      });
      setSavedSnapshot(snapshot);
      setSaveStatus("saved");
    } catch (err) {
      setSaveStatus("error");
      throw err;
    }
  }, [readOnly, isSuggestionMode, isDirty, emailTo, emailCc, emailBcc, emailSubject, emailContent, email.id, token]);

  const applySuggestion = useCallback((subject: string, content: string) => {
    setEmailSubject(subject);
    setEmailContent(content);
    void resetEditorContent(content);
    setSavedSnapshot({ to: emailTo, cc: emailCc, bcc: emailBcc, subject, content });
    setSaveStatus("idle");
  }, [emailTo, emailCc, emailBcc, resetEditorContent]);

  useEffect(() => {
    if (readOnly || isSuggestionMode || !isDirty || saveStatus === "saving") return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      handleSave().catch(() => { /* status already set to "error" */ });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [readOnly, isSuggestionMode, isDirty, saveStatus, emailTo, emailCc, emailBcc, emailSubject, emailContent, handleSave]);

  useImperativeHandle(ref, () => ({
    flushSave: handleSave,
    isDirty: () => isDirty,
    applySuggestion,
  }), [handleSave, isDirty, applySuggestion]);

  const labelClass = isSuggestionMode ? AI_PREVIEW_LABEL_CLASS : EMAIL_PREVIEW_LABEL_CLASS;
  const fieldClass = (ro: boolean) => isSuggestionMode ? aiPreviewFieldClass() : emailPreviewFieldClass(ro);
  const canToggleRecipientRows = !readOnly && !isSuggestionMode;
  const showRecipientToggleButtons = canToggleRecipientRows || isSuggestionMode;
  const hasCcContent = !!emailCc.trim();
  const hasBccContent = !!emailBcc.trim();
  const showCcRow = showRecipientToggleButtons ? showCc : !!emailCc.trim();
  const showBccRow = showRecipientToggleButtons ? showBcc : !!emailBcc.trim();
  const recipientToggleButtonClass = (active: boolean, canHide: boolean, interactive: boolean) => {
    if (!interactive) {
      return `text-xs font-gilroy font-semibold ${active ? "text-[#1875FB]" : "text-[#1875FB]/60"} cursor-default`;
    }

    return `text-xs font-gilroy font-semibold transition-colors ${active
      ? (canHide ? "text-blue hover:text-gray-400" : "text-blue cursor-default")
      : "text-gray-400 hover:text-blue"
    }`;
  };
  const handleCcToggleClick = () => {
    if (!canToggleRecipientRows)
      return;

    if (!showCcRow) {
      setShowCc(true);
      return;
    }
    if (!hasCcContent)
      setShowCc(false);
  };
  const handleBccToggleClick = () => {
    if (!canToggleRecipientRows)
      return;

    if (!showBccRow) {
      setShowBcc(true);
      return;
    }
    if (!hasBccContent)
      setShowBcc(false);
  };
  const showToolbar = !readOnly;

  const displaySubject = showSuggestionContent
    ? (suggestionPreview?.suggestion?.proposedSubject ?? "")
    : emailSubject;

  const displayContentHtml = showSuggestionContent
    ? sanitizeHtml(suggestionPreview?.suggestion?.proposedContent ?? "")
    : sanitizeHtml(emailContent);

  const revealClass = (delayed: boolean) =>
    `${delayed ? "suggestion-content-in-delayed" : "suggestion-content-in"}${contentRevealed ? " is-revealed" : ""}`;

  const saveButtonState: "saving" | "error" | "pending" | "saved" =
    saveStatus === "saving"
      ? "saving"
      : saveStatus === "error"
        ? "error"
        : isDirty
          ? "pending"
          : "saved";

  const saveLabel = isSuggestionMode
    ? t("Saved")
    : saveButtonState === "saving"
      ? t("Saving") + "..."
      : saveButtonState === "error"
        ? t("Retry save")
        : saveButtonState === "pending"
          ? t("Save")
          : t("Saved");

  const saveBtnDisabled = isSuggestionMode || saveButtonState === "saving" || saveButtonState === "saved";

  const saveBtnClass = isSuggestionMode
    ? "portant-ai-gradient-border text-gray-500 cursor-default"
    : saveButtonState === "error"
      ? "bg-white text-red border border-red hover:bg-red hover:text-white"
      : saveButtonState === "saving"
        ? "bg-gray-50 text-gray-400 border border-gray-200 cursor-default"
        : saveButtonState === "pending"
          ? "bg-blue text-white border border-transparent hover:opacity-90 cursor-pointer"
          : "bg-gray-50 text-gray-400 border border-gray-200 cursor-default";

  const outerClass = isSuggestionMode
    ? `${AI_PREVIEW_OUTER_CLASS} ${AI_PREVIEW_SHELL_ENTER_CLASS} relative`
    : "flex flex-col h-full overflow-hidden bg-white border border-gray-200 rounded";

  const bodyFrameClass = isSuggestionMode
    ? aiPreviewBodyFrameClass()
    : emailPreviewBodyFrameClass(!!readOnly);
  const maybeWrapSuggestionField = (input: ReactElement) => (
    isSuggestionMode
      ? <div className="flex-1 min-w-0 rounded-lg portant-ai-field-surface">{input}</div>
      : input
  );

  return (
    <div className={outerClass}>
      {isGenerating && <div className="suggestion-shimmer-overlay" aria-hidden />}

      <div className={EMAIL_PREVIEW_HEADER_CLASS}>
        <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
          <label className={labelClass}>{t("From")}:</label>
          {maybeWrapSuggestionField(
            <input
              type="text"
              readOnly
              value={formatWorkflowUserSender(email.sender)}
              className={fieldClass(true)}
            />
          )}
        </div>

        <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
          <label className={labelClass}>{t("To")}:</label>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {maybeWrapSuggestionField(
              <input
                type="text"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                readOnly={readOnly || isSuggestionMode}
                className={fieldClass(!!readOnly || isSuggestionMode)}
              />
            )}
            {showRecipientToggleButtons &&
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className={recipientToggleButtonClass(showCcRow, !hasCcContent, canToggleRecipientRows)}
                  onClick={handleCcToggleClick}
                  aria-disabled={!canToggleRecipientRows || (showCcRow && hasCcContent)}
                >
                  {t("Cc")}
                </button>
                <button
                  type="button"
                  className={recipientToggleButtonClass(showBccRow, !hasBccContent, canToggleRecipientRows)}
                  onClick={handleBccToggleClick}
                  aria-disabled={!canToggleRecipientRows || (showBccRow && hasBccContent)}
                >
                  {t("Bcc")}
                </button>
              </div>
            }
          </div>
        </div>

        {showCcRow &&
          <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
            <label className={labelClass}>{t("Cc")}:</label>
            {maybeWrapSuggestionField(
              <input
                type="text"
                value={emailCc}
                onChange={e => setEmailCc(e.target.value)}
                readOnly={readOnly || isSuggestionMode}
                className={fieldClass(!!readOnly || isSuggestionMode)}
              />
            )}
          </div>
        }

        {showBccRow &&
          <div className={EMAIL_PREVIEW_FIELD_ROW_CLASS}>
            <label className={labelClass}>{t("Bcc")}:</label>
            {maybeWrapSuggestionField(
              <input
                type="text"
                value={emailBcc}
                onChange={e => setEmailBcc(e.target.value)}
                readOnly={readOnly || isSuggestionMode}
                className={fieldClass(!!readOnly || isSuggestionMode)}
              />
            )}
          </div>
        }

        <div className={`${EMAIL_PREVIEW_FIELD_ROW_CLASS}${showSuggestionContent ? ` ${revealClass(false)}` : ""}`}>
          <label className={labelClass}>{t("Subject")}:</label>
          {maybeWrapSuggestionField(
            <input
              type="text"
              value={displaySubject}
              onChange={e => setEmailSubject(e.target.value)}
              readOnly={readOnly || isSuggestionMode}
              className={fieldClass(!!readOnly || isSuggestionMode)}
            />
          )}
        </div>
      </div>

      <div className={EMAIL_PREVIEW_BODY_OUTER_CLASS}>
        <div className={bodyFrameClass}>
          {isSuggestionMode ? (
            <div
              className={`${AI_PREVIEW_BODY_CONTENT_CLASS}${showSuggestionContent ? ` ${revealClass(true)}` : ""}`}
              dangerouslySetInnerHTML={{ __html: displayContentHtml }}
            />
          ) : (
            <div className={readOnly ? EMAIL_PREVIEW_BODY_CONTENT_CLASS : EMAIL_PREVIEW_BODY_CONTENT_EDITABLE_CLASS}>
              {RichTextfield}
            </div>
          )}
          {showToolbar && (
            <div className={`flex items-center gap-1 px-2 py-1.5 border-t flex-wrap${isSuggestionMode ? " border-[#1875FB]/20 bg-white/40 pointer-events-none opacity-60" : " border-gray-200 bg-gray-50"}`}>
              <FormatButton
                label={t("Bold")}
                active={activeFormats.bold}
                onTrigger={() => applyFormat("bold")}
              >
                <span className="text-sm font-bold leading-none">B</span>
              </FormatButton>
              <FormatButton
                label={t("Italic")}
                active={activeFormats.italic}
                onTrigger={() => applyFormat("italic")}
              >
                <span className="text-sm italic font-serif leading-none">I</span>
              </FormatButton>
              <FormatButton
                label={t("Underline")}
                active={activeFormats.underline}
                onTrigger={() => applyFormat("underline")}
              >
                <span className="text-sm underline leading-none">U</span>
              </FormatButton>
              <FormatButton
                label={t("Bulleted list")}
                onTrigger={() => applyFormat("insertUnorderedList")}
              >
                <ListBulletIcon className="w-4 h-4" />
              </FormatButton>
              <FormatButton
                label={t("Numbered list")}
                onTrigger={() => applyFormat("insertOrderedList")}
              >
                <ListNumberedIcon className="w-4 h-4" />
              </FormatButton>

              {!isSuggestionMode && (
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => { void handleSave(); }}
                    disabled={saveBtnDisabled}
                    className={`h-7 px-3 min-w-[90px] text-xs font-semibold font-gilroy rounded flex items-center justify-center gap-1 transition-colors ${saveBtnClass}`}
                  >
                    {saveLabel}
                    {saveButtonState === "saved" && <CheckIcon className="w-3 h-3 text-green" />}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pt-2 pb-4 space-y-1.5 shrink-0">
        <div className="flex items-center justify-between gap-2">
          <label className={`text-xs ${isSuggestionMode ? "text-[#1875FB]" : "text-gray-400"}`}>{t("Attachments")}</label>
          <span className="text-xs text-gray-400">
            {t("Editing attachments only available in")}{" "}
            <a
              className="inline-flex items-center gap-0.5 text-blue hover:underline"
              href={email.automationItem ? `https://app.portant.co/outputs/${email.automationItem}` : "https://app.portant.co"}
              target="_blank"
              rel="noreferrer"
            >
              {t("Portant")}
              <ExternalLinkIcon className="w-3 h-3 shrink-0" />
            </a>
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {email.attachments && email.attachments.length > 0 ? (
            email.attachments.map((att, attIndex) => {
              const displayName = getAttachmentDisplayName(att, outputs, attIndex);
              return (
                <TruncateTooltip
                  key={att.id}
                  label={displayName}
                  className="flex items-center gap-2 text-sm border border-gray-200 rounded px-2 py-1"
                  textClassName="text-gray-700 truncate max-w-[200px]"
                  leading={
                    <img
                      className="w-4 h-4 rounded shrink-0"
                      src={MIMETYPE_ICON_MAP[att.attachmentMimeType] || MIMETYPE_ICON_MAP["application/vnd.google-apps.file"]}
                      alt=""
                    />
                  }
                />
              );
            })
          ) : (
            <div className={`flex items-center gap-2 text-sm rounded px-2 py-1 ${isSuggestionMode ? "portant-ai-field-surface" : "border border-gray-200"}`}>
              <span className="text-gray-400">{t("No attachments")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default OutputEmailEditor;
